
import { useState, useEffect, useRef } from 'react';

const team = [
  {
    name: 'Richard Okuti',
    role: 'Team Lead',
    company: '@Asili_FORTUNE',
    email: 'okuti@asili.co.ug',
    bio: "Richard has 20+ years' experience in entrepreneurship, private sector development, project formulation and implementation, and public sector business and trade policy development. With practical experience in 7+ countries digitising business processes, creating and growing working market systems, and supporting the internationalisation of leading sectors.",
    credentials: [
      '20+ years in entrepreneurship and private sector development',
      'Practical experience across 7+ countries',
      'Expert in digitising business processes and market systems',
      'Member of Uganda Business Process Outsourcing and Innovation Council',
      'Board member of ATIS',
      'AIESEC Alumnus',
    ],
    image:
      'https://readdy.ai/api/search-image?query=Professional%20African%20business%20leader%20entrepreneur%20in%20dark%20formal%20suit%20confident%20executive%20portrait%20modern%20minimalist%20office%20setting%20warm%20golden%20hour%20lighting%20experienced%20team%20leader%20corporate%20headshot%20dark%20background%20premium%20photography&width=400&height=500&seq=team-richard-v3&orientation=portrait',
    icon: 'ri-user-star-line',
    color: '#038C7F',
  },
  {
    name: 'Aaron Tukei',
    role: 'Digital Marketer & Operations Specialist',
    company: 'Asili Fortune',
    bio: 'At Asili Fortune, Aaron combines digital marketing, sales, and creative content production to drive brand growth and engagement. He manages daily operations, coordinates deliveries, and shoots high-quality visuals that showcase products and services. His role bridges strategy and hands‑on execution.',
    credentials: [
      'Digital marketing strategy and execution',
      'Sales and client relationship management',
      'Creative content production and photography',
      'Operations management and delivery coordination',
      'Bridges strategy and hands‑on execution',
    ],
    image:
      'https://readdy.ai/api/search-image?query=Young%20African%20digital%20marketing%20professional%20creative%20content%20creator%20with%20professional%20camera%20equipment%20modern%20dark%20workspace%20casual%20business%20attire%20confident%20photographer%20marketer%20portrait%20warm%20lighting%20premium%20photography&width=400&height=500&seq=team-aaron-v3&orientation=portrait',
    icon: 'ri-megaphone-line',
    color: '#F2780C',
  },
  {
    name: 'Fiona Akitua',
    role: 'Social Media Manager',
    company: 'Äsili Visuals',
    bio: 'Fiona leads social media strategy, crafting engaging content that connects brands with their audiences across digital platforms. Her expertise in community management and content creation drives meaningful engagement and brand loyalty across East Africa.',
    credentials: [
      'Social media strategy and planning',
      'Content creation and curation',
      'Community management and engagement',
      'Analytics and performance tracking',
      'Brand voice development',
    ],
    image:
      'https://readdy.ai/api/search-image?query=Professional%20African%20woman%20social%20media%20manager%20working%20on%20laptop%20creative%20modern%20workspace%20dark%20office%20setting%20confident%20digital%20marketer%20portrait%20warm%20natural%20lighting%20elegant%20business%20casual%20attire%20premium%20photography&width=400&height=500&seq=team-fiona-v3&orientation=portrait',
    icon: 'ri-chat-smile-3-line',
    color: '#038C7F',
  },
  {
    name: 'Pius Ekamu',
    role: 'Web Developer & Founder of Ekamutech',
    company: 'Ekamutech',
    website: 'https://ekamu.tech',
    bio: 'Pius is a skilled web developer and the founder of Ekamutech, specializing in building modern, responsive websites and web applications. His technical expertise ensures digital solutions are cutting‑edge, performant, and user‑friendly.',
    credentials: [
      'Full‑stack web development',
      'Modern web technologies and frameworks',
      'Responsive design and UX optimization',
      'Founder of Ekamutech',
      'Technical architecture and implementation',
    ],
    image:
      'https://readdy.ai/api/search-image?query=African%20software%20developer%20web%20programmer%20working%20on%20computer%20multiple%20screens%20coding%20workspace%20modern%20dark%20tech%20office%20professional%20developer%20portrait%20focused%20concentration%20warm%20accent%20lighting%20premium%20photography&width=400&height=500&seq=team-pius-v3&orientation=portrait',
    icon: 'ri-code-s-slash-line',
    color: '#F2780C',
  },
];

export default function TeamMembers() {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // ---------------------------------------------------------------------------
  // IntersectionObserver – show the section when it scrolls into view
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  // ---------------------------------------------------------------------------
  // Helper – safely open external links
  // ---------------------------------------------------------------------------
  const safeOpen = (url: string) => {
    try {
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (win) win.opener = null;
    } catch (e) {
      console.error('Failed to open link:', e);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="team"
      className="relative py-16 sm:py-20 lg:py-28 bg-[#FAFAF9] overflow-hidden"
    >
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#038C7F]/[0.04] to-transparent rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-[#F2780C]/[0.04] to-transparent rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F2780C]/10 rounded-full mb-4">
            <i className="ri-team-line text-[#F2780C] text-sm" />
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-[#F2780C] uppercase">
              Meet The Team
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0D0D0D] leading-tight mb-3">
            The People Behind{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
              Äsili Visuals
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto">
            A diverse team of creative professionals, strategists, and innovators dedicated to
            building exceptional brands across East Africa
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          {team.map((member, index) => {
            const isExpanded = expandedMember === index;
            return (
              <div
                key={index}
                className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-2xl transition-all duration-500 cursor-default ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + index * 150}ms` }}
              >
                {/* Image + overlay */}
                <div className="relative h-64 sm:h-72 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
                  {/* Icon badge */}
                  <div
                    className="absolute top-4 right-4 w-11 h-11 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/20"
                    style={{ backgroundColor: `${member.color}30` }}
                  >
                    <i className={`${member.icon} text-xl text-white`} />
                  </div>

                  {/* Bottom overlay info */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-end justify-between">
                      <div>
                        <h3 className="text-xl sm:text-2xl font-black text-white mb-0.5">
                          {member.name}
                        </h3>
                        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: member.color }}>
                          {member.role}
                        </div>
                        {member.company && (
                          <div className="text-[10px] text-white/60 mt-0.5">{member.company}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                            title={member.email}
                          >
                            <i className="ri-mail-line text-sm text-white" />
                          </a>
                        )}
                        {member.website && (
                          <button
                            onClick={() => safeOpen(member.website!)}
                            className="w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                            title={member.website}
                            type="button"
                          >
                            <i className="ri-links-line text-sm text-white" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bio & credentials */}
                <div className="p-5 sm:p-6">
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{member.bio}</p>

                  {/* Expand / collapse button */}
                  <button
                    onClick={() => setExpandedMember(isExpanded ? null : index)}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"
                    style={{ color: member.color }}
                    type="button"
                  >
                    <span>{isExpanded ? 'Hide Credentials' : 'View Credentials'}</span>
                    {/* NOTE: The className must be a template literal – using backticks */}
                    <i
                      className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-base transition-transform`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      {member.credentials.map((credential, idx) => (
                        <div key={idx} className="flex items-start gap-2.5">
                          <div
                            className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: `${member.color}12` }}
                          >
                            <i className="ri-check-line text-xs" style={{ color: member.color }} />
                          </div>
                          <span className="text-xs text-gray-600 leading-relaxed">{credential}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Join Team CTA */}
        <div
          className={`mt-12 sm:mt-16 transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative bg-[#0D0D0D] rounded-2xl p-6 sm:p-8 lg:p-10 overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#038C7F]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#F2780C]/10 rounded-full blur-2xl" />
            <div className="absolute top-4 left-4 w-14 h-14 border-l-2 border-t-2 border-white/10 rounded-tl-xl hidden sm:block" />
            <div className="absolute bottom-4 right-4 w-14 h-14 border-r-2 border-b-2 border-white/10 rounded-br-xl hidden sm:block" />

            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#038C7F]/20 rounded-xl flex items-center justify-center">
                    <i className="ri-user-add-line text-xl text-[#038C7F]" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-white">Join Our Team</h3>
                </div>
                <p className="text-sm text-gray-400 max-w-lg">
                  We&apos;re always looking for talented individuals who share our passion for creativity
                  and excellence. Ready to make an impact across East Africa?
                </p>
              </div>
              <a
                href="mailto:okuti@asili.co.ug"
                className="group relative px-8 py-3.5 bg-[#038C7F] text-white text-sm font-bold rounded-full overflow-hidden whitespace-nowrap flex items-center gap-2"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-mail-send-line text-lg" />
                  Get In Touch
                </span>
                <div className="absolute inset-0 bg-[#F2780C] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
